'use client';

import React, { useState, useEffect, useRef } from 'react';
import { WATERMARK_SCREENSHOT_STYLE } from '@/lib/watermark-badge';
import { useWatermark } from '@/app/contexts/WatermarkContext';
import { useAdmin } from '@/lib/hooks/useAdmin';

const STORAGE_KEY_POSITION = 'watermark_position';
const STORAGE_KEY_SIZE = 'watermark_size';

const RESIZE_SENSITIVITY = 200;

export default function Watermark() {
  const isAdmin = useAdmin();
  const { isVisible } = useWatermark();
  const [position, setPosition] = useState({ x: 16, y: 16 });
  const [size, setSize] = useState({ scale: 1 }); // Используем scale для масштабирования
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, scale: 1 });
  const watermarkRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const prevVisibleRef = useRef(false);

  // Загружаем сохраненную позицию и размер
  useEffect(() => {
    if (isAdmin && typeof window !== 'undefined') {
      const savedSize = localStorage.getItem(STORAGE_KEY_SIZE);
      if (savedSize) {
        try {
          const { scale } = JSON.parse(savedSize);
          setSize({ scale });
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      }
    }
  }, [isAdmin]);

  // Позиционируем плашку под кнопкой при каждом показе
  useEffect(() => {
    if (isAdmin && isVisible && typeof window !== 'undefined') {
      // Если плашка только что стала видимой (была скрыта, теперь показана)
      const justBecameVisible = !prevVisibleRef.current && isVisible;
      prevVisibleRef.current = isVisible;

      if (justBecameVisible) {
        // Небольшая задержка, чтобы DOM успел обновиться
        const timer = setTimeout(() => {
          const button = document.querySelector('[data-watermark-toggle-button]') as HTMLElement;
          if (button) {
            const rect = button.getBoundingClientRect();
            // Позиционируем плашку под кнопкой с небольшим отступом
            const newPosition = {
              x: rect.left,
              y: rect.bottom + 8, // 8px отступ под кнопкой
            };
            setPosition(newPosition);
          }
        }, 100);

        return () => clearTimeout(timer);
      }
    } else if (!isVisible) {
      prevVisibleRef.current = false;
    }
  }, [isAdmin, isVisible]);

  // Сохраняем позицию и размер в localStorage
  useEffect(() => {
    if (isAdmin && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(position));
    }
  }, [position, isAdmin]);

  useEffect(() => {
    if (isAdmin && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SIZE, JSON.stringify(size));
    }
  }, [size, isAdmin]);

  // Обработка начала перетаскивания
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isAdmin) return;
    e.preventDefault();
    setIsDragging(true);
    if (watermarkRef.current) {
      const rect = watermarkRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  // Обработка движения мыши при перетаскивании
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Обработка изменения размера
  const handleResizeStart = (e: React.MouseEvent) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      scale: size.scale,
    });
  };

  // Обработка движения мыши при изменении размера (с rAF для плавности)
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!watermarkRef.current) return;
      
      // Отменяем предыдущий rAF если он в очереди
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      rafRef.current = requestAnimationFrame(() => {
        // Простая и предсказуемая логика: 
        // тащим влево-вверх = увеличиваем, вправо-вниз = уменьшаем
        const delta = (resizeStart.x - e.clientX) + (resizeStart.y - e.clientY);
        const newScale = Math.max(0.5, Math.min(2.5, resizeStart.scale + delta / RESIZE_SENSITIVITY));
        setSize({ scale: newScale });
      });
    };

    const handleMouseUp = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isResizing, resizeStart]);

  // Не показываем, если не админ или не видима
  if (!isAdmin || !isVisible) return null;

  return (
    <div
      ref={watermarkRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 99999,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        transform: `scale(${size.scale})`,
        transformOrigin: 'top left',
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Видимый grip для изменения размера в левом верхнем углу (появляется при hover) */}
      <div
        style={{
          position: 'absolute',
          left: '-6px',
          top: '-6px',
          width: '14px',
          height: '14px',
          cursor: 'nwse-resize',
          zIndex: 100000,
          display: isDragging ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isHovered || isResizing ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
        onMouseDown={handleResizeStart}
      >
        {/* Три точки-grip */}
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <circle cx="2" cy="2" r="1.2" fill="rgba(255,255,255,0.6)" />
          <circle cx="6" cy="2" r="1.2" fill="rgba(255,255,255,0.6)" />
          <circle cx="2" cy="6" r="1.2" fill="rgba(255,255,255,0.6)" />
        </svg>
      </div>
      {/* Same signature as every export: logo + brand on one row, domain under both. */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: `${WATERMARK_SCREENSHOT_STYLE.lineGap}px`,
        fontFamily: WATERMARK_SCREENSHOT_STYLE.fontFamily,
        whiteSpace: 'nowrap',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: `${WATERMARK_SCREENSHOT_STYLE.gap}px` }}>
          <img src="/iconb-mark.png" alt="Bastion" style={{ width: `${WATERMARK_SCREENSHOT_STYLE.iconSize}px`, height: `${WATERMARK_SCREENSHOT_STYLE.iconSize}px`, flexShrink: 0 }} />
          <span style={{ fontSize: `${WATERMARK_SCREENSHOT_STYLE.fontSize}px`, fontWeight: WATERMARK_SCREENSHOT_STYLE.fontWeight, color: WATERMARK_SCREENSHOT_STYLE.brandColor, letterSpacing: WATERMARK_SCREENSHOT_STYLE.letterSpacing, lineHeight: 1 }}>
            {WATERMARK_SCREENSHOT_STYLE.label}
          </span>
        </span>
        <span style={{ fontSize: `${WATERMARK_SCREENSHOT_STYLE.subFontSize}px`, fontWeight: WATERMARK_SCREENSHOT_STYLE.subFontWeight, color: WATERMARK_SCREENSHOT_STYLE.subColor, lineHeight: 1 }}>
          {WATERMARK_SCREENSHOT_STYLE.subLabel}
        </span>
      </div>
    </div>
  );
}

